import React, { useEffect } from 'react';
import { Row, Col, FormGroup, Label } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import translations from 'services/translations/translations';
import CreatableDropdown from 'components/layout/forms/CreatableDropdown';
import TextInputField from 'components/layout/forms/TextInputField';
import SelectInputFieldControlled from 'components/layout/forms/SelectInputFieldControlled';

import { fetchTaxRateTypes } from 'store/projects/actions';

import IOffer from './IOffer';
import { showPaymentTerms } from './OfferUtils';

const OfferInvoicingInfo = ({
  formState,
  handleFormState,
  locked,
  paymentTerms,
  handlePaymentTerm,
  paymentTerm,
}: {
  formState: IOffer;
  handleFormState: any;
  locked: boolean;
  paymentTerms: any;
  handlePaymentTerm: any;
  paymentTerm: any;
}): JSX.Element | null => {
  const dispatch = useDispatch();
  const { t } = useTranslation([
    translations.offers,
    translations.common,
    translations.products,
  ]);

  useEffect(() => {
    dispatch(fetchTaxRateTypes());
  }, []);

  const taxRateTypes = useSelector((state: any) => state.projects.taxRateTypes);

  const offerIsLocked = () => locked;

  // An offer saved with the earlier VAT rate (24 %) keeps it until another rate is chosen
  const taxRates: string[] = (taxRateTypes ?? []).map(String);
  if (formState.taxRate !== '' && !taxRates.includes(formState.taxRate)) {
    taxRates.push(formState.taxRate);
  }

  // The locale keys use a decimal comma, because a dot separates keys in i18next
  const getTaxRateLabel = (taxRate: string) =>
    t(`${translations.offers}:taxRateTypes.${taxRate.replace('.', ',')}`);

  return (
    <>
      <Row>
        <Col>
          <h3>
            {t(`${translations.offers}:Invoicing information`).toUpperCase()}
          </h3>
        </Col>
      </Row>
      <Row>
        <Col>
          <TextInputField
            value={formState.electronicInvoicingAddress}
            label={t(`${translations.offers}:electronicInvoicingAddress`)}
            required
            id="electronicInvoicingAddress"
            name="electronicInvoicingAddress"
            onChange={handleFormState}
            disabled={offerIsLocked()}
          />
        </Col>
        <Col>
          <TextInputField
            value={formState.electronicInvoicingOperator}
            label={t(`${translations.offers}:electronicInvoicingOperator`)}
            required
            id="electronicInvoicingOperator"
            name="electronicInvoicingOperator"
            onChange={handleFormState}
            disabled={offerIsLocked()}
          />
        </Col>
        <Col>
          <TextInputField
            value={formState.electronicInvoicingOperatorId}
            label={t(`${translations.offers}:electronicInvoicingOperatorId`)}
            required
            id="electronicInvoicingOperatorId"
            name="electronicInvoicingOperatorId"
            onChange={handleFormState}
            disabled={offerIsLocked()}
          />
        </Col>
        <Col>
          <TextInputField
            value={formState.invoiceReference}
            label={t(`${translations.offers}:invoiceReference`)}
            required
            id="invoiceReference"
            name="invoiceReference"
            onChange={handleFormState}
            disabled={offerIsLocked()}
          />
        </Col>
      </Row>
      <Row>
        <Col>
          <SelectInputFieldControlled
            value={formState.taxRate}
            label={t(`${translations.offers}:Tax Rate`)}
            required
            id="taxRate"
            name="taxRate"
            onChange={handleFormState}
            options={[
              <option key="taxRateTypes-empty" value="">
                -
              </option>,
              ...taxRates.map((taxRate) => (
                <option key={`taxRateTypes-${taxRate}`} value={taxRate}>
                  {getTaxRateLabel(taxRate)}
                </option>
              )),
            ]}
            disabled={offerIsLocked()}
          />
        </Col>
        <Col>
          <FormGroup>
            <Label className="label">
              {t(`${translations.offers}:Payment Term`)}
            </Label>
            <CreatableDropdown
              className="text-field search-field"
              placeholder={t(`${translations.offers}:Search or type`)}
              required
              name="paymentTerm"
              value={paymentTerm}
              onChange={handlePaymentTerm}
              options={
                paymentTerms
                  ? showPaymentTerms(paymentTerms, translations, t)
                  : ''
              }
              disabled={offerIsLocked()}
            />
          </FormGroup>
        </Col>
      </Row>
    </>
  );
};

export default OfferInvoicingInfo;
